import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

/**
 * Modal for declining a service request with an optional reason.
 *
 * Props:
 *   isOpen         — boolean
 *   toggle         — function to close the modal
 *   serviceRequest — the request being declined
 *   onDecline      — (payload) => void, called with { serviceRequestId, reason }
 *   isSubmitting   — boolean
 */
const DeclineServiceRequestModal = ({
  isOpen,
  toggle,
  serviceRequest,
  onDecline,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onDecline({
      serviceRequestId: serviceRequest?.id,
      reason: reason.trim() || null,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('business.components.decline_modal.title', 'Decline Service Request')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={toggle}
            aria-label={t('shared.close', 'Close')}
          />
        </div>
        <div className="modal-body">
          <p className="mb-3">
            {t('business.components.decline_modal.confirm_text', 'Are you sure you want to decline request')}{" "}
            <strong>{serviceRequest?.requestNumber}</strong>?
          </p>
          {serviceRequest?.title && (
            <p className="text-muted small mb-3">
              <strong>{t('shared.title', 'Title')}:</strong> {serviceRequest.title}
            </p>
          )}
          <div className="form-group">
            <label htmlFor="declineReason" className="form-label fw-medium">
              {t('shared.reason_optional', 'Reason (optional)')}
            </label>
            <textarea
              id="declineReason"
              className="form-control"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('business.components.decline_modal.placeholder', 'Provide a reason for declining...')}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={toggle}
            disabled={isSubmitting}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('shared.declining', 'Declining...')}
              </>
            ) : (
              t('business.components.decline_modal.button_decline', 'Decline Request')
            )}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default DeclineServiceRequestModal;
